import React from "react";
import styled, { css } from 'styled-components';
import { tokens } from '../../public/tokens.js';

const {
  colors,
  typography,
  globals,
  effects,
} = tokens;

/**
 * Button style variants
 */
export const buttonVariants = {
  primary: css`
    background: ${colors.theme['button-color']};
    color: ${colors.neutral.white};

    &:hover:not(:disabled) {
      background: ${colors.theme['button-hover-color']};
    }

    &:active:not(:disabled) {
      background: ${colors.theme['button-active-color']};
    }
  `,

  theme: css`
    background: ${colors.theme['topbar-color']};
    color: ${colors.neutral.white};

    &:hover:not(:disabled) {
      background: ${colors.theme['button-hover-color']};
    }

    &:active:not(:disabled) {
      background: ${colors.theme['button-active-color']};
    }
  `,

  dark: css`
    background: ${colors.neutral.grey['900']};
    color: ${colors.neutral.white};

    &:hover:not(:disabled) {
      background: ${colors.neutral.grey['700']};
    }

    &:active:not(:disabled) {
      background: ${colors.neutral.grey['500']};
    }
  `,

  white: css`
    background: ${colors.neutral.white};
    color: ${colors.text.primary};
    border: 1px solid ${colors.surfaces['border-divider']};

    &:hover:not(:disabled) {
      background: ${colors.neutral.grey['100']};
    }

    &:active:not(:disabled) {
      background: ${colors.neutral.grey['200']};
    }
  `,
};

/**
 * Button size variants
 */
export const buttonSizes = {
  sm: css`
    min-height: 32px;
    padding: 8px 12px;
    font-size: ${typography.fontSize['md-12px']};
  `,

  md: css`
    min-height: 36px;
    padding: 10px 15px;
    font-size: ${typography.fontSize['lg-14px']};
  `,

  lg: css`
    min-height: 42px;
    padding: 12px 18px;
    font-size: ${typography.fontSize['base-16px']};
  `,

  icon: css`
    width: 36px;
    height: 36px;
    padding: 0;
    border-radius: 50%;
  `,
};

/**
 * Styled Button
 */
const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};
  min-width: 86px;
  border: none;
  outline: none;
  cursor: pointer;
  text-decoration: none;

  transition: all 0.2s ease;
  font-family: ${globals['font-family']};
  font-weight: ${typography.fontWeights.semibold};
  line-height: ${typography.lineHeights['110']};
  text-transform: capitalize;

  border-radius: ${globals['border-radius']};
  box-shadow: ${effects.effects.shadow.default} !important;

  ${({ variant = 'primary' }) => buttonVariants[variant]}
  ${({ size = 'md' }) => buttonSizes[size]}

  &:hover:not(:disabled) {
    box-shadow: ${effects.effects.shadow.hover} !important;
  }

  &:active:not(:disabled) {
    box-shadow: ${effects.effects.shadow.hover} !important;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
  }

  @media (max-width: 575px) {
    font-size: ${typography.fontSize['sm-10px']};
  }

  @media (min-width: 576px) and (max-width: 991px) {
    font-size: ${typography.fontSize['md-12px']};
  }
`;

/**
 * Reusable Button component
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.variant - Button style variant
 * @param {string} props.size - Button size variant
 * @returns {JSX.Element}
 */
const Button = ({ children, variant = "primary", size = "md", ...restProps }) => {
  return (
    <StyledButton variant={variant} size={size} {...restProps}>
      {children}
    </StyledButton>
  );
};

export default Button;